/**
 * Value flow of a trace: what every account ended up with once the whole
 * chain of transactions settled. Mirrors the table of the same name in the
 * acton explorer.
 *
 * The native balance comes from the account states toncenter attaches to
 * each transaction: the first known balance before and the last known
 * balance after, per account, in lt order. Jetton movements come from the
 * successful actions of the trace — the states know nothing about tokens.
 */

const toBigInt = function toBigIntOrUndefined(value) {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    try {
        return BigInt(value);
    } catch (error) {
        return undefined;
    }
};

// A state without a balance is an account that isn't there (yet, or any more):
const stateBalance = function balanceOfAccountState(state) {
    const balance = toBigInt(state?.balance);

    if (balance !== undefined) {
        return balance;
    }

    return state?.account_status === 'nonexist' ? 0n : undefined;
};

// How much the transaction itself moved the balance: what came in, minus the
// fees and everything sent out (forward fees ride along with the messages).
// Lets a missing state on one side be worked out from the other -- toncenter
// often has no state for an account it saw only in passing, and the sum has
// held up against every indexed transaction it was checked on.
const balanceDelta = function balanceDeltaOfTransaction(tx) {
    const incoming = toBigInt(tx.in_msg?.value) ?? 0n;
    const fees = toBigInt(tx.total_fees) ?? 0n;
    const outgoing = (tx.out_msgs || []).reduce((sum, msg) => sum
        + (toBigInt(msg?.value) ?? 0n)
        + (toBigInt(msg?.fwd_fee) ?? 0n)
        + (toBigInt(msg?.ihr_fee) ?? 0n), 0n);

    return incoming - fees - outgoing;
};

const compareLt = (a, b) => {
    const left = toBigInt(a.lt) ?? 0n;
    const right = toBigInt(b.lt) ?? 0n;
    if (left === right) return 0;
    return left < right ? -1 : 1;
};

/**
 * Token transfers a successful action implies, as (asset, amount, from, to).
 * Swaps are two transfers: the one into the pool and the one out of it.
 * Native TON legs (asset null) are skipped — they are already in the balances.
 */
const actionMovements = function jettonMovementsOfAction(action) {
    const details = action?.details || {};

    const movement = (asset, amount, source, destination) => (
        asset && amount && (source || destination)
            ? [{ asset, amount, source, destination }]
            : []
    );

    switch (action?.type) {
        case 'jetton_transfer':
            return movement(details.asset, details.amount, details.sender, details.receiver);

        case 'jetton_mint':
            return movement(details.asset, details.amount, undefined, details.receiver);

        case 'jetton_burn':
            return movement(details.asset, details.amount, details.owner, undefined);

        case 'jetton_swap':
        case 'tonco_jetton_swap': {
            const incoming = details.dex_incoming_transfer;
            const outgoing = details.dex_outgoing_transfer;
            return [
                ...movement(incoming?.asset, incoming?.amount, incoming?.source, incoming?.destination),
                ...movement(outgoing?.asset, outgoing?.amount, outgoing?.source, outgoing?.destination),
            ];
        }

        default:
            return [];
    }
};

/**
 * @param  {Object} options.transactions  trace transactions keyed by hash, as toncenter returns them
 * @param  {Array}  options.actions       trace actions (raw toncenter shape, extra fields are ignored)
 * @param  {Object} options.addressBook   raw address -> { user_friendly }
 * @param  {Object} options.metadata      raw address -> { token_info: [...] }
 * @return {Object} { rows, assets, totalFee } — rows in order of first appearance,
 *                  assets are the jetton columns, amounts are BigInt in the smallest unit
 */
export const buildValueFlow = function buildValueFlowOfTrace({ transactions = {}, actions = [], addressBook = {}, metadata = {} } = {}) {
    const rows = new Map();
    const assets = new Map();

    const row = (address) => {
        if (!rows.has(address)) {
            const book = addressBook[address] || {};
            rows.set(address, {
                address,
                friendly: book.user_friendly || address,
                before: undefined,
                after: undefined,
                fee: 0n,
                changes: new Map(),
            });
        }
        return rows.get(address);
    };

    Object.values(transactions)
        .filter(tx => tx?.account)
        .sort(compareLt)
        .forEach((tx) => {
            const account = row(tx.account);
            const knownBefore = stateBalance(tx.account_state_before);
            const knownAfter = stateBalance(tx.account_state_after);
            const delta = balanceDelta(tx);

            let before = knownBefore;
            let after = knownAfter;
            if (before === undefined && after !== undefined) before = after - delta;
            if (after === undefined && before !== undefined) after = before + delta;

            if (account.before === undefined) account.before = before;
            if (after !== undefined) account.after = after;
            account.fee += toBigInt(tx.total_fees) ?? 0n;
        });

    actions
        .filter(action => action?.success !== false)
        .flatMap(actionMovements)
        .forEach(({ asset, amount, source, destination }) => {
            const value = toBigInt(amount);
            if (value === undefined || value <= 0n) {
                return;
            }

            if (!assets.has(asset)) {
                const info = metadata[asset]?.token_info?.[0];
                assets.set(asset, {
                    address: asset,
                    friendly: addressBook[asset]?.user_friendly || asset,
                    symbol: info?.symbol || undefined,
                    decimals: Number.isFinite(Number(info?.extra?.decimals)) ? Number(info.extra.decimals) : 9,
                });
            }

            [[source, -value], [destination, value]]
                .filter(([address]) => address)
                .forEach(([address, delta]) => {
                    const { changes } = row(address);
                    changes.set(asset, (changes.get(asset) ?? 0n) + delta);
                });
        });

    const assetList = [...assets.values()];

    const list = [...rows.values()]
        .map(account => ({
            ...account,
            change: account.before !== undefined && account.after !== undefined
                ? account.after - account.before
                : undefined,
            changes: assetList.map(asset => account.changes.get(asset.address) ?? 0n),
        }))
        // An account that only shows up as a jetton counterparty without a
        // transaction of its own (a mint's receiver, say) still has a row,
        // but one with neither a balance nor a token change says nothing:
        .filter(account => account.change !== undefined || account.changes.some(delta => delta !== 0n));

    return {
        rows: list,
        assets: assetList,
        totalFee: list.reduce((sum, account) => sum + account.fee, 0n),
    };
};
